import { FC, memo, useId } from 'react';

type IconShareProps = {
  size?: number;
  width?: number;
  height?: number;
};

/**
 * Kept locally because the component library has no share icon.
 * The fill uses `currentColor` to match the Figma export, so it still tracks
 * `ButtonV2`'s `--icon-color`.
 * The `size || width` fallback matches the library's own icons (e.g.
 * IconInfo), so passing `size={0}` still yields the default size, per the
 * existing calling convention.
 * Swap this out for a real import as soon as one is added to the library.
 */
export const IconShare: FC<IconShareProps> = memo(
  ({ size, width = 16, height = 16 }) => {
    const clipId = useId();
    return (
      <svg
        width={size || width}
        height={size || height}
        viewBox='0 0 16 16'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
        aria-hidden='true'
        focusable='false'
      >
        <g clipPath={`url(#${clipId})`}>
          <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M9.99913 12.0605L5.36198 9.74284C4.89102 10.2547 4.21749 10.5769 3.46701 10.577L3.3342 10.5736C2.01592 10.5068 0.959003 9.45003 0.892358 8.13173L0.888885 7.99978C0.889061 6.57627 2.04345 5.42166 3.46701 5.42166L3.59895 5.42513C4.29617 5.46038 4.91879 5.77439 5.36198 6.25586L10.0017 3.93641C9.98136 3.82598 9.96501 3.71363 9.9592 3.59874L9.95573 3.4668C9.95573 2.04313 11.1102 0.888672 12.5339 0.888672L12.6658 0.892144C14.028 0.961008 15.1111 2.0875 15.1111 3.4668L15.1076 3.59874C15.0388 4.96081 13.913 6.04387 12.5339 6.04405L12.401 6.04058C11.7035 6.00527 11.0812 5.69078 10.638 5.20898L6 7.52756C5.99951 7.52494 5.99875 7.52237 5.99826 7.51975C6.0276 7.67536 6.04425 7.83566 6.04427 7.99978L6.0408 8.13173C6.03498 8.2468 6.01868 8.35966 5.99826 8.47027L10.638 10.7897C10.6312 10.7971 10.6248 10.8048 10.6181 10.8123C11.09 10.2873 11.7725 9.95551 12.5339 9.95551L12.6658 9.95898C14.028 10.0279 15.1111 11.1543 15.1111 12.5336L15.1076 12.6656C15.0387 14.0276 13.9129 15.1107 12.5339 15.1109L12.401 15.1074C11.0828 15.0407 10.0259 13.9838 9.9592 12.6656L9.95573 12.5336C9.95573 12.3721 9.97069 12.2139 9.99913 12.0605ZM12.5339 11.3774C11.8957 11.3774 11.3776 11.8954 11.3776 12.5336C11.3779 13.1716 11.8958 13.689 12.5339 13.689C13.1717 13.6888 13.689 13.1715 13.6892 12.5336C13.6892 11.8956 13.1719 11.3776 12.5339 11.3774ZM3.46701 6.84353C2.82892 6.84353 2.31094 7.36174 2.31076 7.99978C2.31095 8.63782 2.82893 9.15516 3.46701 9.15516C4.10493 9.15498 4.6222 8.6377 4.62239 7.99978C4.62222 7.36185 4.10494 6.84372 3.46701 6.84353ZM12.5339 2.31055C11.8957 2.31055 11.3776 2.8286 11.3776 3.4668C11.3778 4.10483 11.8958 4.62218 12.5339 4.62218C13.1718 4.62199 13.689 4.10472 13.6892 3.4668C13.6892 2.82872 13.1719 2.31073 12.5339 2.31055Z'
            fill='currentColor'
          />
        </g>
        <defs>
          <clipPath id={clipId}>
            <rect width='16' height='16' fill='white' />
          </clipPath>
        </defs>
      </svg>
    );
  }
);
IconShare.displayName = 'IconShare';
